import {
  CalendarOutlined,
  ContactsOutlined,
  TeamOutlined,
  UserOutlined,
  ProductOutlined,
  HomeOutlined,
  BarChartOutlined,
  AppstoreOutlined,
  SettingOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import PATHS from "../shared/constants/paths";
import { FULL_VIEWPORT_HEIGHT } from "../shared/constants/zoom";
import { UserType } from "../shared/types/enum";
import React, { useState } from "react";
import { Layout, Menu } from "antd";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import SpottLogo from "components/logos/SpottLogo.component";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { useAuth } from "context/AuthContextProvider";
import { isManager } from "shared/utils/role";
import { useAdminCapabilities } from "hooks/useAdminCapabilities";
import { hardNavigateIfUpdated } from "shared/utils/appVersion";
import type { GetProp, MenuProps } from "antd";

const { Sider, Content } = Layout;

// Breathing room under the last card of every page, matching the 30px side
// padding below. Without it a long page (Penggajian, a Laporan table, a
// Pengaturan sub-page) scrolled to a white card pressed flat against the
// bottom edge of the viewport, with nothing to say the section had ended.
// Exported for the one page that sizes itself to the shell (Jadwal), which
// has to subtract it or the page gains exactly this much scroll.
export const CONTENT_PADDING_BOTTOM = 30;

type MenuItem = GetProp<MenuProps, "items">[number];

const MainLayout: React.FC<React.PropsWithChildren> = ({ children }) => {
  const navigate = useNavigate();
  let location = useLocation();
  const { state } = useAuth();
  // Pembatasan Admin: this is the one mount of the business-settings query
  // for a plain Admin, so every gated surface finds the flags already cached.
  // Called for that mount alone, not for a value: nothing on this layout
  // reads a capability any more. Do not remove it because it looks unused.
  useAdminCapabilities();
  const [isHovering, setIsHovering] = useState(false);
  // The list view (PATHS.LIST) now lives under the single "Jadwal" nav item, so
  // map it to PATHS.SCHEDULE to keep that item highlighted on both routes.
  // Every /analytics/* route (the index + individual report pages) maps back
  // to the single Analytics nav item the same way.
  // Product detail/create pages map back to their catalog item the same way:
  // /package/:id + /package/new highlight Paket, /membership/:id highlights
  // Membership.
  const selectedKey = location.pathname.startsWith("/analytics")
    ? PATHS.ANALYTICS
    : location.pathname.startsWith(PATHS.PAYROLL)
      ? PATHS.PAYROLL
      // Karyawan is a group (Instruktur / Staf, like Produk). Its two lists
      // are their own keys; a detail page (/staff/:id) lights the list it was
      // opened from, carried as ?type=, and Instruktur otherwise (the
      // analytics and Akun Admin deep links carry no type).
      : location.pathname.startsWith(PATHS.STAFF)
        ? location.pathname === PATHS.KARYAWAN_STAFF ||
          new URLSearchParams(location.search).get("type") === UserType.Staff
          ? PATHS.KARYAWAN_STAFF
          : PATHS.KARYAWAN_INSTRUCTORS
      : // Every /settings/* page is reached from the Pengaturan launcher, so
        // Pengaturan stays lit on all of them. Without this the loyalty and
        // referral pages dropped the highlight entirely. Akun Admin is one of
        // those launcher pages too, but it kept its own /accounts route from
        // when it was a top-level item, so it is named here explicitly.
        location.pathname.startsWith(PATHS.SETTINGS) ||
          location.pathname.startsWith(PATHS.ACCOUNTS)
        ? PATHS.SETTINGS
        : location.pathname.startsWith(PATHS.MEMBERSHIP)
          ? PATHS.MEMBERSHIP
          : location.pathname.startsWith(PATHS.PACKAGE)
            ? PATHS.PACKAGE
            : location.pathname === PATHS.LIST
              ? PATHS.SCHEDULE
              : location.pathname;

  const menuItems: MenuItem[] = [
    {
      key: PATHS.DASHBOARD,
      label: translate("dashboard.name"),
      icon: <HomeOutlined />,
    },
    {
      // Single top-level Jadwal item — the in-page Kalender/Daftar toggle now
      // handles the two views, so the submenu is redundant. Both routes stay
      // registered; PATHS.LIST maps to this item via selectedKey above.
      key: PATHS.SCHEDULE,
      label: translate("schedule.name"),
      icon: <CalendarOutlined />,
    },
    {
      key: PATHS.CLASS,
      label: translate("class"),
      icon: <AppstoreOutlined />,
    },
    {
      key: "product",
      label: translate("product"),
      icon: <ProductOutlined />,
      children: [
        {
          key: PATHS.PACKAGE,
          label: translate("package"),
        },
        {
          key: PATHS.MEMBERSHIP,
          label: translate("membership"),
        },
      ],
    },
    {
      key: PATHS.CUSTOMER,
      label: translate("customer"),
      icon: <ContactsOutlined />,
    },
    {
      // Every employee of the studio, one list per type (the same group shape
      // as Produk above). Detail routes stay under /staff/:id.
      key: "karyawan",
      label: translate("karyawan"),
      icon: <TeamOutlined />,
      children: [
        {
          key: PATHS.KARYAWAN_INSTRUCTORS,
          label: translate("karyawan_type.instructor"),
        },
        {
          key: PATHS.KARYAWAN_STAFF,
          label: translate("karyawan_type.staff"),
        },
      ],
    },
    // Separates the surfaces you operate on day to day (schedule, classes,
    // products, clients, employees) from the ones you review and configure
    // below. Inset + the design system's translucent-ink border token
    // (colors.line) instead of AntD's default edge-to-edge grey rule, to
    // match the rest of the app's "borders over shadows" look.
    {
      type: "divider",
      key: "settings-divider",
      style: { margin: "8px 20px", borderColor: colors.line },
    },
    // "Laporan" (Analytics) sits below the line next to Penggajian because
    // neither is operational: one is read periodically, the other is run once
    // a pay period. It leads the lower block as the only item there that
    // every admin sees, so a plain Admin (no Penggajian) still finds an item
    // directly under the divider. The key doubles as the navigate target, and
    // every admin now shares one: they all land on the Laporan index, which
    // decides for itself which reports to list, by capability.
    {
      key: PATHS.ANALYTICS,
      label: translate("analyticsPage.nav_label"),
      icon: <BarChartOutlined />,
    },
    // "Akun Admin" is no longer a top-level item: it is a Manager-only
    // configuration list, so it lives in Pengaturan under Tim & Akses beside
    // Pembatasan Admin (who the admins are, next to what they may do). Its
    // /accounts route is unchanged; selectedKey above keeps Pengaturan lit.
    // "Penggajian" (Payroll) covers commission rates, running payroll and
    // payslips. Manager-only, server-enforced too (isManagerMiddleware on
    // every /v1/payroll route). Kept next to "Laporan" above rather than
    // folded into Karyawan or Laporan: it's its own multi-step workflow
    // (settings -> rates -> run -> history), not a sub-page of an existing
    // concern.
    ...(isManager(state.user?.roles)
      ? [
          {
            key: PATHS.PAYROLL,
            label: translate("payrollPage.nav_label"),
            icon: <WalletOutlined />,
          },
        ]
      : []),
    // Business-wide booking policy (cancellation/waitlist/schedule
    // visibility). Moved out of Kelas, where it read as class-specific when
    // it's actually business-wide. Now also the home of Akun Admin, under
    // Tim & Akses: who the admins are sits next to what they may do, and the
    // hub grows by gaining a row rather than by adding another sidebar item.
    {
      key: PATHS.SETTINGS,
      label: translate("class_settings.tab.settings"),
      icon: <SettingOutlined />,
    },
    {
      // Bisnis is read-only reference info (business identity), not
      // something an Admin/Manager can actually manage — grouping it with
      // the personal "Profil Aku" page under one "Akun" umbrella keeps both
      // as "look up who/what this is" pages, rather than falsely implying
      // Bisnis is manageable.
      key: "account",
      label: translate("account"),
      icon: <UserOutlined />,
      children: [
        {
          key: PATHS.BUSINESS_PROFILE,
          label: translate("business"),
        },
        {
          key: PATHS.USER_PROFILE,
          label: translate("tabs.my_profile"),
        },
      ],
    },
  ];

  if (location.pathname === PATHS.LOGIN) {
    return <Navigate to={PATHS.HOME} />;
  }

  return (
    <Layout style={{ minHeight: FULL_VIEWPORT_HEIGHT }}>
      <Sider
        theme="light"
        collapsedWidth="70"
        collapsed={!isHovering}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <SpottLogo style={{ margin: 20 }} collapsed={!isHovering} />
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          onClick={(e) => {
            // A sidebar click is the safe moment to pick up a new deploy: it
            // already abandons the current page, so a full page load here can
            // never lose anything. AntD fires onClick for leaf items only, so
            // e.key is always a real route path.
            if (hardNavigateIfUpdated(e.key)) return;
            navigate(e.key);
          }}
        />
      </Sider>
      <Layout
        style={{
          // Lighter warm off-white content surface (#FAFAF7) — NOT the darker
          // colorBgLayout (#EFEEE9), which is the --page-outer frame color.
          backgroundColor: colors.page,
          paddingLeft: 30,
          paddingRight: 30,
          paddingBottom: CONTENT_PADDING_BOTTOM,
        }}
      >
        <Content>{children}</Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
